import { useEffect, useState } from 'react';
import { Box, Button, Typography } from '@material-ui/core';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import clsx from 'clsx';
import moment from 'moment';
import { MdLogin } from 'react-icons/md';
import DefaultScreen from '../../DefaultScreen';
import { Title } from '../../../components/managment/Title';
import { BaseDialog } from '../../../components/DialogTemplates/BaseDialog';
import { logout } from '../../../helpers/Api/PulseemReactAPI';
import { disconnectMySso, getMySso } from '../../../redux/reducers/MySsoSlice';

/**
 * Settings > SSO login: the logged-in user (main login or SubUser) connects THEIR OWN company login once,
 * like the Authenticator app setup. "Connect my SSO" opens the web site's /Sso/Connect, which sends the user to the
 * company login (set up by the account admin in Settings > SSO (SAML)) and saves the identity that comes back.
 * Then: login page > SSO > work email > company login > Pulseem.
 * API: WebSiteAPI Controllers/Settings/MySsoController.cs. The result of a connect comes back as ?ssoConnect=...
 */
const box: React.CSSProperties = { border: '1px solid var(--pls-d-border, #ddd)', borderRadius: 8, padding: '14px 18px', maxWidth: 760, marginTop: 16, background: 'var(--pls-d-surface, #fff)' };
const helpBox: React.CSSProperties = { background: 'var(--pls-d-surface-2, #f4f8fc)', border: '1px solid var(--pls-d-border, #c9dcef)', borderRadius: 8, padding: '12px 18px', maxWidth: 760, lineHeight: 1.6 };
const valueBox: React.CSSProperties = { fontFamily: 'Consolas, monospace', direction: 'ltr', background: 'var(--pls-d-surface-2, #f3f3f3)', border: '1px solid var(--pls-d-border, #ddd)', borderRadius: 4, padding: '1px 6px' };
const CONNECT_RESULTS = ['ok', 'taken', 'off', 'notallowed', 'failed'];
const SSO_ONLY_MODE = 2; // SubAccountSsoConfig.Mode: 0 Off, 1 Optional, 2 SSO only

const MySso = ({ classes }: any) => {
    const { t } = useTranslation();
    const dispatch = useDispatch();
    const { isRTL, windowSize } = useSelector((state: any) => state.core);

    const [data, setData] = useState<any>(null);
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState('');
    const [message, setMessage] = useState('');
    const [confirmDisconnect, setConfirmDisconnect] = useState(false);

    const tr = (key: string, options?: any) => t(`settings.mySso.${key}`, options) as string;

    /** Calls the API: payload on 201, otherwise shows the translated error and returns null. */
    const call = async (action: any) => {
        setBusy(true);
        setError('');
        try {
            const payload = ((await dispatch(action)) as any)?.payload;
            if (payload?.StatusCode === 401) {
                logout();
                return null;
            }
            if (payload?.StatusCode !== 201) {
                const key = `errors.${payload?.Message || 'general'}`;
                const text = tr(key);
                setError(text === `settings.mySso.${key}` ? tr('errors.general') : text);
                return null;
            }
            return payload;
        } finally {
            setBusy(false);
        }
    };

    const load = async () => {
        const payload = await call(getMySso());
        if (payload) setData(payload.Data);
    };

    useEffect(() => {
        // Back from the web site's "Connect my SSO": show the result once, then clean the address
        const result = new URLSearchParams(window.location.search).get('ssoConnect');
        if (result) {
            const text = tr(`result.${CONNECT_RESULTS.includes(result) ? result : 'failed'}`);
            if (result === 'ok') setMessage(text); else setError(text);
            window.history.replaceState(null, '', window.location.pathname);
        }
        load();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const onConnect = () => {
        if (!data?.ConnectUrl) return;
        setBusy(true);
        window.location.href = `${data.ConnectUrl}?ReturnUrl=${encodeURIComponent(window.location.pathname)}`;
    };

    const onDisconnect = async () => {
        setConfirmDisconnect(false);
        setMessage('');
        const payload = await call(disconnectMySso());
        if (payload) {
            setData(payload.Data);
            setMessage(tr('msgDisconnected'));
        }
    };

    const button = (text: string, onClick: () => void, outlined = false) => (
        <Button className={clsx(classes.btn, classes.btnRounded)} variant={outlined ? 'outlined' : undefined}
            style={{ marginInlineEnd: 10, marginTop: 10 }} disabled={busy} onClick={onClick}>
            {text}
        </Button>
    );

    const renderStatus = () => {
        const connected = !!data.ConnectedEmail;
        return (
            <Box style={box}>
                {connected ? (
                    <>
                        <Typography style={{ fontWeight: 'bold', color: 'var(--pls-d-success, #1b7f3b)' }}>
                            {tr('connectedAs')} <span style={valueBox}>{data.ConnectedEmail}</span>
                        </Typography>
                        {data.ConnectedDate && (
                            <Typography style={{ color: 'var(--pls-d-text-2, #555)', fontSize: 14, marginTop: 4 }}>
                                {tr(data.ConnectedByUser ? 'connectedOnByYou' : 'connectedOnByAdmin', { date: moment(data.ConnectedDate).format('DD/MM/YYYY') })}
                            </Typography>
                        )}
                        {data.Available && data.ConnectedEmail.includes('@') && (
                            <Typography style={{ marginTop: 10 }}>{tr('howToLogin', { email: data.ConnectedEmail })}</Typography>
                        )}
                    </>
                ) : (
                    <Typography style={{ fontWeight: 'bold' }}>{tr('notConnected')}</Typography>
                )}

                {!data.Available && <Typography style={{ color: 'var(--pls-d-warning, #8a5a00)', marginTop: 10 }}>{tr(`reasons.${data.Reason || 'NOT_SET_UP'}`)}</Typography>}

                {data.Available && (
                    <>
                        <Typography style={{ color: 'var(--pls-d-text-2, #555)', fontSize: 14, marginTop: 10 }}>{tr('connectNote')}</Typography>
                        <Box>
                            {button(connected ? tr('reconnect') : tr('connect'), onConnect)}
                            {connected && data.CanDisconnect && button(tr('disconnect'), () => setConfirmDisconnect(true), true)}
                        </Box>
                    </>
                )}
                {connected && !data.CanDisconnect && data.Mode === SSO_ONLY_MODE && (
                    <Typography style={{ color: 'var(--pls-d-text-2, #555)', fontSize: 14, marginTop: 10 }}>{tr('ssoOnlyNote')}</Typography>
                )}
            </Box>
        );
    };

    return (
        <DefaultScreen currentPage="settings" subPage="mySso" key="mySso" classes={classes} containerClass={clsx(classes.management, classes.mb50)}>
            <Box className={'topSection'}>
                <Box className={clsx('head')}>
                    <Title
                        classes={classes}
                        isIcon={false}
                        ContainerStyle={{ padding: windowSize === 'xs' ? '10px 15px' : '11px 31.69px 8px 31.69px' }}
                        Element={
                            <Box className={clsx(classes.dFlex)} alignItems="center">
                                <MdLogin size={24} style={{ marginInlineEnd: 10 }} />
                                <Typography className={clsx(classes.managementTitle, 'mgmtTitle')} style={{ width: 'auto' }}>{tr('title')}</Typography>
                            </Box>
                        }
                    />
                </Box>
            </Box>
            <Box style={{ padding: windowSize === 'xs' ? '10px 15px' : '10px 32px', direction: isRTL ? 'rtl' : 'ltr' }}>
                <Box style={helpBox}><Typography>{tr('help')}</Typography></Box>
                {message && <Typography style={{ color: 'var(--pls-d-success, #1b7f3b)', fontWeight: 'bold', marginTop: 12 }}>{message}</Typography>}
                {error && <Typography style={{ color: 'var(--pls-d-danger, #c00)', fontWeight: 'bold', marginTop: 12 }}>{error}</Typography>}
                {data && renderStatus()}
            </Box>
            <BaseDialog classes={classes} open={confirmDisconnect} title={tr('disconnect')}
                onClose={() => setConfirmDisconnect(false)} onCancel={() => setConfirmDisconnect(false)} onConfirm={onDisconnect}>
                <Typography>{tr('disconnectConfirm')}</Typography>
            </BaseDialog>
        </DefaultScreen>
    );
};

export default MySso;
